/* eslint-disable react/prop-types */
import React, { useState, createRef, useEffect } from "react"; //import the component part of React
//Packages
import axios from "axios";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";
import { isAuthenticated } from "./services/auth"; //import auth
//Images and Icons
import { MdOutlineCancel, MdAddCircle, MdManageSearch } from "react-icons/md";
// Common
import StatusBlock from "../common/StatusBlock";
import FadeIn from "../common/FadeIn";

const VenueList = () => {
  const [venues, setVenues] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [createMode, setCreateMode] = useState(false);

  // Get the Venue Details and request new if Success.
  useEffect(async () => {
    const venuesRequest = await axios.get(`/api/admin/manage/venue`, {
      withCredentials: true,
    });
    return setVenues(venuesRequest.data);
  }, []);

  //Fetch New Data if there's a successful change.
  const fetchNewData = async () => {
    const venuesReRequest = await axios.get(`/api/admin/manage/venue`, {
      withCredentials: true,
    });
    return setVenues(venuesReRequest.data);
  };

  //Delete A Venue
  const deleteVenue = async (_id) => {
    const deleteRequest = await axios.delete(`/api/admin/manage/venue/${_id}`, {
      withCredentials: true,
    });

    if (deleteRequest.status === 200) {
      setSuccessMessage("Venue was successfully deleted.");
      return fetchNewData();
    } else {
      return setErrorMessage(
        "There was a problem with your request. It is likely there are still classes attached to this venue."
      );
    }
  };

  // CreateRef to handle Alerts and scroll
  const alerts = createRef();
  const scrollSmoothHandler = () => {
    return alerts.current.scrollIntoView({ behavior: "smooth" });
  };

  // Dismiss Message
  const dismissMessages = () => {
    setSuccessMessage("");
    return setErrorMessage("");
  };

  return (
    <div className='VenueList'>
      <FadeIn delay={250} duration={450}>
        {isAuthenticated() ? (
          <Container>
            <Row className='mt-4 mb-4 text-center'>
              <Col sm='12'>
                <h2>Your Venues</h2>
                <div ref={alerts}>
                  <StatusBlock
                    errorMessage={errorMessage}
                    successMessage={successMessage}
                    dismissMessages={dismissMessages}
                  />
                </div>
                {!createMode && (
                  <FadeIn delay={50} duration={450}>
                    {" "}
                    <Button variant='primary' onClick={() => setCreateMode(true)}>
                      <MdAddCircle />
                      Add Venue
                    </Button>
                  </FadeIn>
                )}
                {createMode && (
                  <FadeIn delay={50} duration={450}>
                    <Button variant='secondary' onClick={() => setCreateMode(false)}>
                      <MdOutlineCancel />
                      Return
                    </Button>
                  </FadeIn>
                )}
              </Col>
            </Row>
            {createMode && (
              <FadeIn delay={50} duration={450}>
                <VenueCreate
                  fetchNewData={fetchNewData}
                  setErrorMessage={setErrorMessage}
                  setSuccessMessage={setSuccessMessage}
                  scrollSmoothHandler={scrollSmoothHandler}
                />
              </FadeIn>
            )}
            <Row className='mt-4 mb-4 text-center'>
              {venues.map((Venue) => (
                <Col key={Venue._id} sm='12' lg='6'>
                  <FadeIn delay={50} duration={450}>
                    <div className='mx-1 mt-3 p-2 shadow border'>
                      <h3>{Venue.VenueName}</h3>
                      <p>{Venue.VenueAddress}</p>
                      <Button variant='primary' as={Link} to={Venue._id}>
                        <MdManageSearch /> Manage
                      </Button>{" "}
                      <Button
                        variant='danger'
                        onClick={(e) => {
                          deleteVenue(Venue._id, e);
                          scrollSmoothHandler();
                        }}
                      >
                        Delete <MdOutlineCancel />
                      </Button>
                    </div>
                  </FadeIn>
                </Col>
              ))}
            </Row>
          </Container>
        ) : (
          <Navigate to='/admin/' />
        )}
      </FadeIn>
    </div>
  );
};

const VenueCreate = (props) => {
  const [VenueName, setVenueName] = useState("");
  const [VenueAddress, setVenueAddress] = useState("");
  const [Note, setNote] = useState("None");
  const [submitClicked, setSubmitClicked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      VenueName: VenueName,
      VenueAddress: VenueAddress,
      Note: Note,
    };

    //Set Submit Clicked
    setSubmitClicked(true);

    const venueCreateRequest = await axios.post("/api/admin/manage/venue", data, {
      withCredentials: true,
    });

    if (venueCreateRequest.status === 200) {
      props.setSuccessMessage("Venue successfully created.");
      setSubmitClicked(false);
      return props.fetchNewData();
    } else {
      setSubmitClicked(false);
      return props.setErrorMessage("There was a problem with your request.");
    }
  };

  return (
    <Row className='mt-4 mb-4'>
      <Col>
        <Form className='p-4 text-center border shadow' onSubmit={handleSubmit}>
          <Row>
            <Col md='6'>
              <Form.Group controlId='VenueNameForm'>
                <Form.Label>Venue Name</Form.Label>
                <Form.Control
                  type='text'
                  maxLength='256'
                  placeholder='Venue Name'
                  value={VenueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  className='border'
                  required
                />
                <Form.Text className='text-muted'>The name of the venue.</Form.Text>
              </Form.Group>
            </Col>
            <Col md='6'>
              <Form.Group controlId='VenueNameForm'>
                <Form.Label>Venue Address</Form.Label>
                <Form.Control
                  type='text'
                  maxLength='256'
                  placeholder='Venue Address'
                  value={VenueAddress}
                  onChange={(e) => setVenueAddress(e.target.value)}
                  className='border'
                  required
                />
                <Form.Text className='text-muted'>The full address of the venue.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm='12'>
              <Form.Label>Venue Note</Form.Label>
              <Form.Control
                as='textarea'
                type='text'
                maxLength='1000'
                value={Note}
                onChange={(e) => setNote(e.target.value)}
                className='border'
                placeholder='None'
                required
              />
              <Form.Text className='text-muted'>
                Up to 1,000 characters to leave a note about the venue. Best to keep it short and
                sweet though. Examples might be parking instructions or other facilities
                information.
              </Form.Text>
            </Col>
          </Row>
          <Col className='mt-2 mb-2'>
            <Button
              variant='success'
              size='lg'
              type='submit'
              onClick={props.scrollSmoothHandler}
              disabled={submitClicked}
            >
              Create Venue
            </Button>
          </Col>
        </Form>
      </Col>
    </Row>
  );
};

export default VenueList;
